import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Download, FileText, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const insides = [
  "The Five Disciplines of Strategic Leadership",
  "The Clarity Pyramid",
  "The Alignment Matrix",
  "The Momentum Loop",
  "The Strategic Decision Filter",
  "A 90-Day Action Plan",
];

const initial = { name: "", email: "" };

export const LeadMagnet = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { download_url }

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        source: "operating_system_download",
      });
      setResult(res.data);
      toast.success("Your PDF is opening in a new tab.");
      // Auto-open PDF in a new tab so they get instant access too
      if (res.data?.download_url) {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="framework"
      data-testid="framework-section"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* LEFT — copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="ssg-hero-rule" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              Free Framework · 17 Pages
            </span>
          </div>
          <h2
            data-testid="framework-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.0]"
          >
            BUILD:<br />
            <span className="italic font-light">The SSG</span><br />
            Operating System.
          </h2>
          <p className="mt-8 text-[17px] leading-relaxed text-neutral-600 font-medium max-w-xl">
            A leadership framework for building better leaders, stronger
            organizations, and lasting impact — the same system I use with
            clients to close the gap between where they are and what
            they&apos;re capable of becoming.
          </p>

          <div className="mt-10">
            <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500 mb-5">
              Inside you&apos;ll find
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {insides.map((item, i) => (
                <li
                  key={item}
                  data-testid={`framework-inside-${i}`}
                  className="flex items-start gap-3 text-sm text-neutral-800 font-medium"
                >
                  <CheckCircle2
                    size={16}
                    strokeWidth={1.6}
                    className="text-[#9B1C1C] mt-0.5 flex-none"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* RIGHT — PDF mock + form card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          {/* Faux PDF cover — sits behind the form card */}
          <div className="hidden md:block absolute -top-8 -left-6 w-56 aspect-[3/4] bg-[#0A0A0A] text-white p-6 shadow-2xl -rotate-6 select-none">
            <div className="text-[9px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              Smith Strategic Group
            </div>
            <div className="mt-6 font-display text-2xl font-black leading-[1.02] tracking-tight">
              BUILD:
            </div>
            <div className="font-display italic font-light text-lg leading-tight mt-1">
              The SSG<br />Operating<br />System
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="w-8 h-px bg-[#9B1C1C] mb-3" />
              <div className="text-[9px] tracking-[0.24em] uppercase font-bold text-neutral-400 leading-tight">
                A leadership<br />framework
              </div>
            </div>
            <div className="absolute -bottom-px -right-px h-10 w-10 bg-[#9B1C1C]" />
          </div>

          {/* Form / success card */}
          <div className="relative bg-[#F7F6F3] p-8 md:p-10 border border-neutral-200 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]">
            {result ? (
              <div data-testid="framework-success">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#9B1C1C] mb-4">
                  <CheckCircle2 size={16} strokeWidth={1.8} />
                  Delivered
                </div>
                <h3 className="font-display text-3xl font-black tracking-tight text-neutral-950 leading-tight">
                  Your download is opening.
                </h3>
                <p className="mt-4 text-[15px] text-neutral-700 leading-relaxed font-medium">
                  The PDF should be opening in a new tab right now. If your
                  browser blocked it, use the button below to grab it.
                </p>
                <a
                  href={result.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="framework-download-again"
                  className="mt-8 inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#9B1C1C] text-white px-7 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  <Download size={16} strokeWidth={1.6} />
                  Download again
                </a>
                <button
                  onClick={() => {
                    setResult(null);
                    setForm(initial);
                  }}
                  data-testid="framework-reset"
                  className="mt-6 ml-4 md:ml-6 text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 hover:text-neutral-900"
                >
                  Send to another email
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="framework-form">
                <div className="flex items-center gap-3 mb-2 text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500">
                  <FileText size={14} strokeWidth={1.6} className="text-[#9B1C1C]" />
                  Get the PDF
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-neutral-950 leading-tight">
                  Free download.<br />
                  <span className="italic font-light">No spam.</span>
                </h3>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed font-medium">
                  Drop your name and email — we&apos;ll send the framework
                  straight to your inbox.
                </p>

                <div className="mt-7 space-y-6">
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-2">
                      Name
                    </span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      required
                      placeholder="Your full name"
                      data-testid="framework-input-name"
                      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-base text-neutral-950 placeholder:text-neutral-400 transition-colors"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-2">
                      Email
                    </span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      placeholder="you@company.com"
                      data-testid="framework-input-email"
                      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-base text-neutral-950 placeholder:text-neutral-400 transition-colors"
                    />
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="framework-submit"
                  className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#9B1C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  {loading ? "Sending…" : "Send Me The PDF"}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.6}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>

                <p className="mt-4 text-[10px] tracking-[0.18em] uppercase font-bold text-neutral-400 text-center">
                  Instant PDF download
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadMagnet;
